/**
 * Gateway Service
 * Handles communication with the Gateway API for topics and status operations
 *
 * ⚠️ SECURITY: ALL Gateway connections MUST be authorized.
 * See /SECURITY_GUIDELINES.md for mandatory security requirements.
 *
 * Authorization modes:
 * - Direct Gateway: Bearer token in Authorization header
 * - Proxy (TYPO3): CSRF token in X-CSRF-Token header + credentials: 'include'
 *
 * State Management:
 * - Updates useGatewayStore for centralized state
 * - Components subscribe to store changes to react to health/connection updates
 */

import { GATEWAY, STORAGE_KEYS } from './app-constants';
import { useGatewayStore } from '../stores/gateway-store';
import { RequestHandler } from './gateway/request-handler';
import { DocumentParserClient } from './gateway/document-parser-client';
import { PollingManager } from './gateway/polling-manager';
import { RetryableRequest } from './gateway/retryable-request';
import { GatewayError, GatewayErrorCode } from './gateway/error-codes';
import { logger } from './logger';
import { splitText } from './gateway/text-chunker';
import type { TextChunk } from './gateway/text-chunker';
import { runChunked } from './gateway/chunked-request-runner';
import {
  mergeCheckRulesResults,
  mergeSessionChunkedResults,
} from './gateway/result-mergers';
import { computeDocumentId } from './gateway/document-id';
import { runSessionChunked } from './gateway/session-chunked-runner';
import type { LabelGroups } from './relations/default-interaction-labels';
import type {
  ApiResponse,
  DiscoveryResponse,
  CheckRulesOptions,
  CheckRulesResponse,
  ExtractChunkResponse,
  ParseOptions,
  AuthService,
  GatewayServiceOptions,
} from './gateway/types';

export type {
  ApiResponse,
  TaskIdResponse,
  DiscoveryDisambiguatedCandidate,
  DiscoveryDisambiguationResult,
  DiscoveryResolverResult,
  DiscoveryResponse,
  StatementDisplay,
  ConflictDisplay,
  SimilarityDisplay,
  CoverageGapDisplay,
  NearMissDisplay,
  CheckRulesOptions,
  CheckRulesResponse,
  ExtractChunkResponse,
  PollStatusInfo,
  HealthState,
  ConnectionState,
  ParseOptions,
  AuthService,
  GatewayServiceOptions,
} from './gateway/types';
export type { LabelGroups } from './relations/default-interaction-labels';

class GatewayService {
  private useProxy: boolean;
  private proxyUrl: string | null;
  private authService: AuthService | null;
  private baseUrl: string;
  private endpoint: string;
  private authToken: string | null;
  private initialized: boolean;
  private requestHandler: RequestHandler;
  private retryableRequest: RetryableRequest;
  private documentParserClient: DocumentParserClient;
  private pollingManager: PollingManager;
  private sessionChunking: boolean;

  constructor(options: GatewayServiceOptions = {}) {
    // Proxy mode configuration
    this.useProxy = options.useProxy !== undefined ? options.useProxy : false;
    this.proxyUrl = options.proxyUrl || null;
    this.authService = options.authService || null; // ✅ SECURITY: Reference to auth service for CSRF tokens
    this.sessionChunking = options.sessionChunking ?? GATEWAY.SESSION_CHUNKING;

    // Allow baseUrl to be configured via options or localStorage
    this.baseUrl =
      options.baseUrl || this.getStoredBaseUrl() || GATEWAY.DEFAULT_BASE_URL;
    this.endpoint = GATEWAY.ENDPOINT;
    this.authToken = null;
    this.initialized = false;

    // Initialize request handler
    this.requestHandler = new RequestHandler({
      useProxy: this.useProxy,
      proxyUrl: this.proxyUrl,
      baseUrl: this.baseUrl,
      endpoint: this.endpoint,
      authService: this.authService,
    });

    this.retryableRequest = new RetryableRequest(this.requestHandler);

    // Initialize document parser client
    this.documentParserClient = new DocumentParserClient({
      useProxy: this.useProxy,
      proxyUrl: this.proxyUrl,
      baseUrl: this.baseUrl,
      endpoint: this.endpoint,
      authService: this.authService,
      authToken: this.authToken,
    });

    // Initialize polling manager
    // Health/connection state changes are written to useGatewayStore
    // Components subscribe to store changes to react to updates
    this.pollingManager = new PollingManager({
      pollingInterval: GATEWAY.DEFAULT_POLLING_INTERVAL_MS,
      maxConsecutiveFailures: GATEWAY.MAX_CONSECUTIVE_FAILURES,
      rateLimitBackoffMs: 300000, // 5 minute backoff for rate limits
      statusProvider: () => this.getStatus(),
      isInitialized: () => this.isInitialized(),
    });

    // Set rate limit callback
    this.requestHandler.setRateLimitCallback(() =>
      this.pollingManager.handleRateLimit()
    );
  }

  /**
   * Get stored base URL from localStorage
   * @returns Stored base URL or null
   */
  getStoredBaseUrl(): string | null {
    if (import.meta.env.PROD) return null;
    try {
      return localStorage.getItem(STORAGE_KEYS.GATEWAY_BASE_URL);
    } catch (error) {
      logger.warn('Unable to access localStorage for base URL:', error);
      return null;
    }
  }

  /**
   * Initialize the Gateway service with authentication token
   * @param authToken - Authentication token for API calls
   * @throws If authToken is not provided
   */
  initialize(authToken: string): void {
    if (
      !authToken ||
      typeof authToken !== 'string' ||
      authToken.trim().length === 0
    ) {
      this.pollingManager.emitConnectionState('disconnected');
      throw new Error('Authentication token is required for Gateway service');
    }
    this.authToken = authToken.trim();
    this.requestHandler.setAuthToken(this.authToken);
    this.documentParserClient.setAuthToken(this.authToken);
    this.initialized = true;

    // Update store
    useGatewayStore.getState().setInitialized(true);
    useGatewayStore.getState().setBaseUrl(this.baseUrl);

    logger.debug('Gateway service initialized successfully');
    this.pollingManager.emitConnectionState('connected');
  }

  /**
   * Initialize the Gateway service for proxy mode. The TYPO3 session (cookie
   * + CSRF token via authService) authorizes each request, so no token is
   * stored here. Health is left to the first poll rather than assumed.
   */
  initializeProxy(): void {
    this.initialized = true;
    useGatewayStore.getState().setInitialized(true);
    logger.debug('Gateway service initialized in proxy mode');
  }

  /**
   * Check if the service is properly initialized
   * @returns True if initialized
   */
  isInitialized(): boolean {
    // In proxy mode, no authToken is needed
    if (this.useProxy) {
      return this.initialized;
    }
    // Direct mode requires both initialized flag and authToken
    return this.initialized && this.authToken !== null;
  }

  /**
   * Make a request to the Gateway API
   * @param command - The command to execute
   * @param language - ISO 639-3 language code
   * @param data - Additional data for the command
   * @returns API response
   * @throws If request fails or service not initialized
   */
  makeRequest(
    command: string,
    language: string = 'eng',
    data: string = '{}',
    signal?: AbortSignal
  ): Promise<ApiResponse> {
    return this.retryableRequest.execute(command, language, data, signal);
  }

  /**
   * Make a proxied request through TYPO3 (exposed for testing)
   * @param command - The command to execute
   * @param language - ISO 639-3 language code
   * @param data - Additional data for the command
   * @returns API response
   * @throws If request fails or user not authenticated
   */
  makeProxyRequest(
    command: string,
    language: string = 'eng',
    data: string = '{}',
    signal?: AbortSignal
  ): Promise<ApiResponse> {
    return this.retryableRequest.execute(command, language, data, signal);
  }

  /**
   * Fetch the gateway `/config/labels` localisation seed data (constant
   * identifier -> suggested English label, grouped). Used to seed the
   * interaction-label cache; callers should tolerate failure and fall back to
   * the bundled defaults.
   */
  async getInteractionLabels(): Promise<LabelGroups> {
    return this.requestHandler.getLabels();
  }

  /**
   * Get status for a topic
   * @param topicId - The topic identifier (optional, may not be needed based on API)
   * @param language - ISO 639-3 language code (optional)
   * @returns Status information
   * @throws If request fails
   */
  async getStatus(
    topicId: string = '',
    language: string = 'eng'
  ): Promise<ApiResponse> {
    try {
      const data = topicId ? JSON.stringify({ topic: topicId }) : '{}';
      const response = await this.makeRequest('health', language, data);
      return response;
    } catch (error) {
      // Don't log rate limit errors (already handled)
      if (!(
        error instanceof GatewayError &&
        error.code === GatewayErrorCode.RATE_LIMITED
      )) {
        logger.error('Failed to get status:', error);
      }
      throw error;
    }
  }

  /**
   * Parse documents using the Gateway API (via proxy or direct)
   * @param arrayBuffer - Document file as ArrayBuffer
   * @param filename - Original filename
   * @param mimeType - MIME type of the document
   * @param options - Parsing options (to_formats, etc.)
   * @returns Task ID for the parsing job
   * @throws If request fails or service not initialized
   */
  async parseDocuments(
    arrayBuffer: ArrayBuffer,
    filename: string,
    mimeType: string,
    options: ParseOptions = {}
  ): Promise<string> {
    return this.documentParserClient.parseDocuments(
      arrayBuffer,
      filename,
      mimeType,
      options
    );
  }

  /**
   * Poll parsing status using the Gateway API
   * @param taskId - Task ID to poll
   * @param language - ISO 639-3 language code (optional)
   * @returns Status response
   * @throws If request fails
   */
  async pollParseStatus(
    taskId: string,
    language: string = 'eng'
  ): Promise<ApiResponse> {
    try {
      const data = JSON.stringify({ task_id: taskId });
      const response = await this.makeRequest('documents.poll', language, data);
      return response;
    } catch (error) {
      logger.error('Failed to poll parse status:', error);
      throw error;
    }
  }

  /**
   * Get parsing result using the Gateway API
   * @param taskId - Task ID to retrieve
   * @param language - ISO 639-3 language code (optional)
   * @returns Parsing result
   * @throws If request fails
   */
  async getParseResult(
    taskId: string,
    language: string = 'eng'
  ): Promise<ApiResponse> {
    try {
      const data = JSON.stringify({ task_id: taskId });
      const response = await this.makeRequest(
        'documents.result',
        language,
        data
      );
      return response;
    } catch (error) {
      logger.error('Failed to get parse result:', error);
      throw error;
    }
  }

  /**
   * Resolve a free-text query via the search command.
   *
   * @param query - Free-text search term (required, non-empty)
   * @param language - ISO 639-3 language code (default: 'eng')
   * @param context - Optional surrounding text to improve disambiguation
   * @param sources - Optional resolver filter (subset of sources result)
   * @param maxDocuments - Optional max documents to return (1-200)
   * @param options - Optional request options (AbortSignal)
   * @returns Discovery response with disambiguation and per-resolver results
   * @throws If request fails, parameters invalid, or service not initialized
   */
  async discover(
    query: string,
    language: string = 'eng',
    context?: string,
    sources?: string[],
    maxDocuments?: number,
    options?: { signal?: AbortSignal }
  ): Promise<DiscoveryResponse> {
    // In proxy mode, initialization is handled by the proxy
    if (!this.useProxy && !this.isInitialized()) {
      throw new Error(
        'Gateway service not initialized. Call initialize() first.'
      );
    }

    if (!query || typeof query !== 'string' || query.trim().length === 0) {
      throw new Error('query must be a non-empty string');
    }

    if (
      maxDocuments !== undefined &&
      (typeof maxDocuments !== 'number' ||
        !Number.isInteger(maxDocuments) ||
        maxDocuments < 1 ||
        maxDocuments > 200)
    ) {
      throw new Error('maxDocuments must be an integer between 1 and 200');
    }

    // `language` travels in the top-level command envelope, not in `data`.
    const discoverData: Record<string, unknown> = { query };
    if (context !== undefined) discoverData.context = context;
    if (sources !== undefined) discoverData.sources = sources;
    if (maxDocuments !== undefined) discoverData.max_documents = maxDocuments;

    try {
      const startMs = performance.now();
      logger.debug(
        `Discovering: query="${query}", language=${language}, ` +
          `sources=${sources?.join(',') ?? 'all'}, ` +
          `max_documents=${maxDocuments ?? 'default'}`
      );

      const response = await this.makeRequest(
        'search',
        language,
        JSON.stringify(discoverData),
        options?.signal
      );

      if (response.status === 'ok' && response.data) {
        const discoveryResponse = response.data as DiscoveryResponse;
        logger.debug(
          `Discovery successful: term=${discoveryResponse.term}, ` +
            `entity_id=${discoveryResponse.entity_id ?? 'none'}, ` +
            `num_resolvers=${discoveryResponse.results?.length ?? 0}`
        );
        const elapsedMs = Math.round(performance.now() - startMs);
        logger.info(
          `[bench] command=search ` +
            `status=200 elapsed_ms=${elapsedMs} ` +
            `resolvers=${discoveryResponse.results?.length ?? 0}`
        );
        return discoveryResponse;
      } else {
        throw new Error('Invalid response from Gateway: unexpected format');
      }
    } catch (error) {
      logger.error('Failed to discover:', error);
      throw error;
    }
  }

  /**
   * Retrieve the list of available search sources.
   *
   * @param language - ISO 639-3 language code (default: 'eng')
   * @returns Array of source names
   * @throws If request fails, service not initialized, or response malformed
   */
  async getSources(language: string = 'eng'): Promise<string[]> {
    // In proxy mode, initialization is handled by the proxy
    if (!this.useProxy && !this.isInitialized()) {
      throw new Error(
        'Gateway service not initialized. Call initialize() first.'
      );
    }

    try {
      logger.debug(`Fetching discovery sources (language=${language})`);

      const response = await this.makeRequest('sources', language, '{}');

      if (response.status === 'ok' && Array.isArray(response.data)) {
        const sources = response.data as string[];
        if (!sources.every((s) => typeof s === 'string')) {
          throw new Error(
            'Invalid response from Gateway: sources must be an array of strings'
          );
        }
        logger.debug(`Retrieved ${sources.length} discovery sources`);
        return sources;
      }

      throw new Error('Invalid response from Gateway: unexpected format');
    } catch (error) {
      logger.error('Failed to retrieve sources:', error);
      throw error;
    }
  }

  /**
   * Extract normative rules from regulatory text and run contradiction/similarity checks.
   *
   * @param text - Regulatory/normative text to analyse
   * @param language - ISO 639-3 language code (default: 'eng')
   * @param options - Optional domain, provenance, check types, abort signal
   * @returns Extraction result with statements, conflicts, and similarities
   */
  async checkRules(
    text: string,
    language: string = 'eng',
    options?: CheckRulesOptions
  ): Promise<CheckRulesResponse> {
    if (!this.useProxy && !this.isInitialized()) {
      throw new Error(
        'Gateway service not initialized. Call initialize() first.'
      );
    }

    if (!text || text.trim().length === 0) {
      throw new Error('Text cannot be empty for rule checking');
    }

    logger.debug(
      `Checking rules: language=${language}, ` +
        `domain=${options?.domain ?? 'default'}, text_length=${text.length}`
    );

    if (text.length <= GATEWAY.CHUNK_TARGET_CHARS) {
      return this.checkRulesSingle(text, language, options);
    }

    const chunks = splitText(
      text,
      GATEWAY.CHUNK_TARGET_CHARS,
      GATEWAY.CHUNK_OVERLAP_CHARS
    );
    logger.info(
      `Rule-check text exceeds limit (${text.length} chars); split into ${chunks.length} chunks`
    );

    if (this.useProxy && this.sessionChunking) {
      return this.checkRulesSessionChunked(text, chunks, language, options);
    }

    return runChunked(
      chunks,
      (chunk) => this.checkRulesSingle(chunk.text, language, options),
      (results, _chunks, failedIndices) =>
        mergeCheckRulesResults(results, chunks, failedIndices),
      {
        onProgress: options?.onProgress,
      }
    );
  }

  private async checkRulesSingle(
    text: string,
    language: string,
    options?: CheckRulesOptions
  ): Promise<CheckRulesResponse> {
    const data: Record<string, unknown> = { text };
    if (options?.domain !== undefined) data.domain = options.domain;
    if (options?.source_uri !== undefined) data.source_uri = options.source_uri;
    if (options?.source_document !== undefined)
      data.source_document = options.source_document;
    if (options?.source_section !== undefined)
      data.source_section = options.source_section;
    if (options?.check !== undefined) data.check = options.check;

    const startMs = performance.now();
    const response = await this.retryableRequest.execute(
      'rules.check',
      language,
      JSON.stringify(data),
      options?.signal,
      options?.onRetry ? { onRetry: options.onRetry } : undefined
    );

    if (response.status === 'ok' && response.data) {
      const result = response.data as CheckRulesResponse;
      if (result.check_error) {
        logger.warn(`rules.check: check step failed: ${result.check_error}`);
      }
      const elapsedMs = Math.round(performance.now() - startMs);
      logger.debug(
        `Rules checked: statement_count=${result.statement_count}, ` +
          `sentence_count=${result.sentence_count}, ` +
          `conflicts=${result.conflicts?.length ?? 0}, ` +
          `similarities=${result.similarities?.length ?? 0}`
      );
      logger.info(
        `[bench] command=rules.check chars=${text.length} ` +
          `status=200 elapsed_ms=${elapsedMs} ` +
          `statements=${result.statement_count ?? 0} ` +
          `contradictions=${result.conflicts?.length ?? 0} ` +
          `similarities=${result.similarities?.length ?? 0}`
      );
      return result;
    }
    throw new Error('Invalid response from Gateway: unexpected format');
  }

  /**
   * Session-scoped chunk loop (#81): each chunk is extracted into a
   * gateway-side workspace keyed by (derived session, document_id) — the
   * session is derived by the gateway from the proxy-forwarded user, the
   * client never handles a session id — then a single check runs over the
   * accumulated document. Statements keep chunk-local spans server-side, so
   * spans are shifted client-side from the per-chunk responses, and the
   * interaction results come from the final check.
   */
  private async checkRulesSessionChunked(
    text: string,
    chunks: TextChunk[],
    language: string,
    options?: CheckRulesOptions
  ): Promise<CheckRulesResponse> {
    const documentId = await computeDocumentId(text);

    return runSessionChunked<ExtractChunkResponse, CheckRulesResponse>(
      chunks,
      (chunk, i) =>
        this.extractRelationsChunk(
          chunk.text,
          documentId,
          `c${i}`,
          i === 0,
          language,
          options
        ),
      () => this.checkRulesAccumulated(documentId, language, options),
      (chunkResults, checkResult, failedIndices) =>
        mergeSessionChunkedResults(
          chunkResults,
          chunks,
          checkResult,
          failedIndices
        ),
      {
        onProgress: options?.onProgress,
      }
    );
  }

  private async extractRelationsChunk(
    text: string,
    documentId: string,
    chunkId: string,
    isFirstChunk: boolean,
    language: string,
    options?: CheckRulesOptions
  ): Promise<ExtractChunkResponse> {
    // is_first_chunk lets the gateway tell a new sequence (may bootstrap the
    // workspace) from a continuation whose workspace was reaped (must fail
    // closed with SESSION_EXPIRED instead of silently starting over) (#94).
    const data: Record<string, unknown> = {
      text,
      document_id: documentId,
      chunk_id: chunkId,
      is_first_chunk: isFirstChunk,
    };
    if (options?.domain !== undefined) data.domain = options.domain;
    if (options?.source_uri !== undefined) data.source_uri = options.source_uri;
    if (options?.source_document !== undefined)
      data.source_document = options.source_document;
    if (options?.source_section !== undefined)
      data.source_section = options.source_section;

    const response = await this.retryableRequest.execute(
      'rules.extract',
      language,
      JSON.stringify(data),
      options?.signal,
      options?.onRetry ? { onRetry: options.onRetry } : undefined
    );

    if (response.status === 'ok' && response.data) {
      return response.data as ExtractChunkResponse;
    }
    throw new Error('Invalid response from Gateway: unexpected format');
  }

  private async checkRulesAccumulated(
    documentId: string,
    language: string,
    options?: CheckRulesOptions
  ): Promise<CheckRulesResponse> {
    // No text: the gateway runs the check over the workspace accumulated
    // under (derived session, document_id). Contract: cross-service session
    // state design, 2026-06-10.
    const data: Record<string, unknown> = { document_id: documentId };
    if (options?.domain !== undefined) data.domain = options.domain;
    if (options?.check !== undefined) data.check = options.check;

    const response = await this.retryableRequest.execute(
      'rules.check',
      language,
      JSON.stringify(data),
      options?.signal,
      options?.onRetry ? { onRetry: options.onRetry } : undefined
    );

    if (response.status === 'ok' && response.data) {
      return response.data as CheckRulesResponse;
    }
    throw new Error('Invalid response from Gateway: unexpected format');
  }

  /**
   * Start polling gateway status
   * @param interval - Polling interval in milliseconds (optional, uses default if not provided)
   */
  startPolling(interval: number | null = null): void {
    this.pollingManager.startPolling(interval);
  }

  /**
   * Stop polling gateway status
   */
  stopPolling(): void {
    this.pollingManager.stopPolling();
  }

  /**
   * Pause polling temporarily (keeps isPolling true)
   */
  pausePolling(): void {
    this.pollingManager.pausePolling();
  }

  /**
   * Resume polling if it was active
   */
  resumePolling(options: { immediate?: boolean } = {}): void {
    this.pollingManager.resumePolling(options);
  }
}

// Export to window for debugging (dev only)
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.GatewayService = GatewayService;
}

export default GatewayService;
